import ApiService from './api.service';

export type SemanticGapType =
  | 'missing_metric'
  | 'missing_dimension'
  | 'missing_relationship'
  | 'missing_source'
  | 'data_quality'
  | 'ambiguous_naming';

export type SemanticGapPriority = 'high' | 'medium' | 'low';

export type SemanticGapStatus =
  | 'detected'
  | 'approved'
  | 'in_progress'
  | 'resolved'
  | 'dismissed';

export type SemanticGap = {
  id: string;
  gap_type: SemanticGapType;
  title: string;
  description: string;
  suggested_resolution?: string | null;
  priority: SemanticGapPriority;
  hit_count: number;
  source_thread_ids: string[];
  status: SemanticGapStatus;
  assigned_to?: string | null;
  resolution_change_request_id?: string | null;
  resolved_at?: string | null;
  created_by?: string | null;
  last_updated?: string | null;
};

type SingleRecordResponse = { record: SemanticGap };
type ListRecordsResponse = {
  records: SemanticGap[];
  total_records?: number;
  total_pages?: number;
};

export class SemanticGapService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
  }): Promise<ListRecordsResponse> {
    const { page = 1, pageSize = 100, filters = {} } = options || {};

    const queryParams: Record<string, any> = {
      page,
      limit: pageSize,
      ...filters,
    };

    return (await ApiService.getInstance().request(
      'GET',
      '/semantic-gaps',
      queryParams
    )) as ListRecordsResponse;
  }

  async findById(id: string): Promise<SemanticGap> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/semantic-gaps/${id}`
    )) as SingleRecordResponse;
    return result.record;
  }

  async update(gap: Partial<SemanticGap> & { id: string }): Promise<SemanticGap> {
    const result = (await ApiService.getInstance().request(
      'PATCH',
      `/semantic-gaps/${gap.id}`,
      gap
    )) as SingleRecordResponse;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request('DELETE', `/semantic-gaps/${id}`);
  }

  async resolve(id: string): Promise<SemanticGap> {
    const result = (await ApiService.getInstance().request(
      'POST',
      `/semantic-gaps/${id}/resolve`
    )) as SingleRecordResponse;
    return result.record;
  }

  async dismiss(id: string): Promise<SemanticGap> {
    const result = (await ApiService.getInstance().request(
      'POST',
      `/semantic-gaps/${id}/dismiss`
    )) as SingleRecordResponse;
    return result.record;
  }

  async approve(id: string): Promise<SemanticGap> {
    const result = (await ApiService.getInstance().request(
      'POST',
      `/semantic-gaps/${id}/approve`
    )) as SingleRecordResponse;
    return result.record;
  }
}

export const semanticGapService = new SemanticGapService();
